import React, {useCallback, useEffect, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import DualListBox from 'react-dual-listbox'
import 'react-dual-listbox/lib/react-dual-listbox.css'
import {useLocation, useNavigate} from 'react-router-dom'
import useSenderIdsOptions from '../../hooks/useSenderIdsOptions'
import axios from 'axios'
import {BASE_URL} from '../config'
import {useAuth} from '../auth'
import {updateUserApi, uploadNidTradeLicence} from './core/userListRequest'
import toast from 'react-hot-toast'
import './user.css'
import {ISenderId} from '../SMSBroadCast/core/_model'
import {convertToBase64, downloadFile, handleAxiosError} from '../../utils/helperFunction/_helper'
import {ICreateUserFormValuesType, initCreateUserValues, paymentTypeOptions} from './core/_model'
import useUserRole from '../../hooks/useUserRole'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import ReactSelectBox from '../../utils/components/ReactSelectBox'
import clsx from 'clsx'
import useUserType from '../../hooks/useUserType'
import PreviewImage from './PreviewImage'
import {getUserAttachment, getValueName} from './core/helper'
import {Helmet} from 'react-helmet-async'

const CREATE_USER_URL = `${BASE_URL}/api/protect/create/user`
const GET_USER_TYPE = `${BASE_URL}/api/protect/getUserType?isCreateUser=1`

const createUserSchema = Yup.object().shape({
  isUpdate: Yup.boolean(),
  // userTypeName: Yup.string().when('isUpdate', {
  //   is: false,
  //   then: (schema) => schema.required('User type is required'),
  //   otherwise: (schema) => schema.notRequired(),
  // }),
  name: Yup.string().required('Username is required'),
  email: Yup.string().required('Email address is required').email('Provide valid email address'),
  number: Yup.string()
    .required('Number is required without country code')
    .matches(/^[0-9]{11}$/, 'Number must be exactly 11 digits'),
  marginType: Yup.string().when('isUpdate', {
    is: false,
    then: (schema) => schema.required('Margin type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  profitPer: Yup.string().when('isUpdate', {
    is: false,
    then: (schema) => schema.required('Profit is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  paymentTypeName: Yup.string().required('Payment type is required'),
  authorRoleName: Yup.string().required('Author Role is required'),
  creditLimit: Yup.string().when('paymentTypeName', {
    is: (val: string) => val?.toLowerCase() === 'postpaid',
    then: (schema) => schema.required('Credit limit is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  profile: Yup.string().required('User image is required'),
  nid: Yup.string().required('NID is required'),
  trade: Yup.string().required('Tradelicense is required'),
  // senders: Yup.array().required('Senders assign is required'),
})
export interface IFileType {
  path?: string
  type?: string
  lastModified?: Date
  lastModifiedDate?: Date
  name?: string | undefined
  size?: number
}

const CreateUser = () => {
  const {auth} = useAuth()
  const {roles} = useUserRole()
  const navigate = useNavigate()
  const {state: updateUser} = useLocation()

  const {senderIdsOptions, allDefaultIds} = useSenderIdsOptions()
  const {userType: usersType} = useUserType({endpoint: GET_USER_TYPE})

  const [nidFile, setNidFile] = useState<File | null>(null)
  const [tradeLicenseFile, setTradeLicenseFile] = useState<File | null>(null)
  const [userImg, setUserImg] = useState<File | null>(null)

  const [selected, setSelected] = useState<string[]>([])
  const [previewFiles, setPreviewFiles] = useState<Record<string, string>>({
    profile: '',
    nid: '',
    trade: '',
  })

  const createUserFormik = useFormik<ICreateUserFormValuesType>({
    initialValues: initCreateUserValues,
    validationSchema: createUserSchema,
    onSubmit: async (values) => {
      try {
        // console.log(values)
        if (!updateUser && selected.length < 1) {
          toast.error('Senders must be assigned')
          return
        }
        const selects = getFormattedSenders(selected, senderIdsOptions)

        let user = {
          userType: values.userType,
          name: values.name,
          email: values.email,
          contactNumber: values.number,
          mergingType: values.marginType,
          profitMargin: values.profitPer,
          sender: selects,
          authorRole: values.authorRole,
          paymentType: values.paymentType,
          ...(values.paymentTypeName?.toLowerCase() === 'postpaid' && {
            creditLimit: values.creditLimit,
          }),
        }

        let formData = new FormData()
        formData.append('nid', nidFile!)
        formData.append('tradlicence', tradeLicenseFile!)
        formData.append('userImg', userImg!)

        const createUser = async () => {
          try {
            toast.loading('Creating User..')
            const res = await axios.post(CREATE_USER_URL, user, {
              headers: {
                Authorization: `Bearer ${auth?.accessToken}`,
              },
            })
            const userId = await res.data.id
            await uploadNidTradeLicence(auth?.accessToken, userId, formData)
            toast.dismiss()
            toast.success('User Created Successfully')
            navigate('/users/list')
            return res
          } catch (err: any) {
            console.log(err)
            toast.dismiss()
            const message = handleAxiosError(err)
            toast.error(message)
          }
        }

        const editUser = async () => {
          const updateValue = {
            name: values.name,
            email: values.email,
            contactNumber: values.number,
            authorRole: values.authorRole,
            paymentType: values.paymentType,
            creditLimit: values.creditLimit,
          }

          try {
            toast.loading('Updating User..')
            const res = await updateUserApi(auth?.accessToken, updateUser.id, updateValue)
            await uploadNidTradeLicence(auth?.accessToken, res.id, formData)
            toast.dismiss()
            toast.success('User Updated Successfully')
            navigate('/users/list')
            return res
          } catch (err: any) {
            console.log(err)
            toast.dismiss()
            const message = handleAxiosError(err)
            toast.error(message)
          }
        }

        if (updateUser) {
          await editUser()
        } else {
          await createUser()
        }
      } catch (error) {
        console.log(error)
        toast.dismiss()
        const message = handleAxiosError(error)
        toast.error(message)
      }
    },
  })

  const MAX_SIZE = 100 * 1024

  const handleMarginTypeChange = (event: {target: {value: React.SetStateAction<string>}}) => {
    createUserFormik.setFieldValue('marginType', event.target.value)
  }

  const onNidDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles[0].size > MAX_SIZE) {
      createUserFormik.setFieldError('nid', 'File size must be less than 100KB')
      toast.error('file size must be less than 100KB')
      return
    }

    setNidFile(acceptedFiles[0])
    const url = await convertToBase64(acceptedFiles[0])
    createUserFormik.setFieldValue('nid', url)
    setPreviewFiles((prevState) => ({
      ...prevState,
      nid: url,
    }))
  }, [])

  const onTradeLicense = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles[0].size > MAX_SIZE) {
      createUserFormik.setFieldError('trade', 'File size must be less than 100KB')
      toast.error('file size must be less than 100KB')
      return
    }

    setTradeLicenseFile(acceptedFiles[0])
    const url = await convertToBase64(acceptedFiles[0])
    createUserFormik.setFieldValue('trade', url)
    setPreviewFiles((prevState) => ({
      ...prevState,
      trade: url,
    }))
  }, [])

  const onProfile = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles[0].size > MAX_SIZE) {
      createUserFormik.setFieldError('profile', 'File size must be less than 100KB')
      toast.error('file size must be less than 100KB')
      return
    }

    setUserImg(acceptedFiles[0])
    const url = await convertToBase64(acceptedFiles[0])
    createUserFormik.setFieldValue('profile', url)
    setPreviewFiles((prevState) => ({
      ...prevState,
      profile: url,
    }))
  }, [])

  const {
    acceptedFiles: nid,
    getRootProps: getContactRootProps,
    getInputProps: getContactInputProps,
    open: openForNid,
  } = useDropzone({
    onDrop: onNidDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
  })
  const {
    acceptedFiles: licenseFiles,
    getRootProps: getLicenseRootProps,
    getInputProps: getLicenseInputProps,
    open: openForLicense,
  } = useDropzone({
    onDrop: onTradeLicense,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
  })
  const {
    acceptedFiles: profilePic,
    getRootProps: getProfileRootProps,
    getInputProps: getProfileInputProps,
    open: openForProfile,
  } = useDropzone({
    onDrop: onProfile,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
  })

  const getFormattedSenders = (selected: any, options: any) => {
    const formattedArr: any = []
    selected.map((el: any) => {
      const newArr = options.filter((opt: any) => el === opt.label)
      return formattedArr.push(newArr[0].id)
    })
    const selectedSenderIds = allDefaultIds.filter((ids: ISenderId) =>
      formattedArr.includes(ids.id)
    )
    return selectedSenderIds
  }

  const onSelectAuthorRole = (id: number) => createUserFormik.setFieldValue('authorRole', id)

  const onSelectPaymentType = (id: number) => createUserFormik.setFieldValue('paymentType', id)

  const onSelectUserType = (id: number) => createUserFormik.setFieldValue('userType', id)

  function base64ToFile(base64String: string, fileName: string, mimeType: string): File {
    // Ensure string is valid base64 (remove prefix if present)
    const cleaned = base64String.replace(/^data:\w+\/[a-zA-Z+.-]+;base64,/, '')

    // Convert base64 → binary
    const byteCharacters = atob(cleaned)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    return new File([byteArray], fileName, {type: mimeType})
  }

  const resetForm = () => {
    createUserFormik.resetForm()
    setNidFile(null)
    setTradeLicenseFile(null)
    setUserImg(null)
  }

  useEffect(() => {
    resetForm()
    if (updateUser) {
      const setValues = async () => {
        try {
          const attachments = await getUserAttachment(updateUser.id)
          // console.log(attachments)
          // console.log(updateUser)
          createUserFormik.setValues({
            ...createUserFormik.values,
            email: updateUser.userEmail,
            number: updateUser.userMobile,
            paymentType: updateUser.payment_type,
            paymentTypeName: getValueName(paymentTypeOptions, updateUser.payment_type),
            creditLimit: updateUser?.credit_limit,
            name: updateUser.user_full_name,
            authorRole: updateUser.author_id,
            authorRoleName: getValueName(roles, updateUser.author_id),
            isUpdate: true,
            profile: attachments?.userprofile!,
            nid: attachments?.usernid!,
            trade: attachments?.usertradelicense!,
          })
          onProfile([
            base64ToFile(
              attachments?.userprofile!,
              `profile_${updateUser.user_full_name}.jpg`,
              'image/jpg'
            ),
          ])
          onNidDrop([
            base64ToFile(
              attachments?.usernid!,
              `nid_${updateUser.user_full_name}.jpg`,
              'image/jpg'
            ),
          ])
          onTradeLicense([
            base64ToFile(
              attachments?.usertradelicense!,
              `tradelicense_${updateUser.user_full_name}.jpg`,
              'image/jpg'
            ),
          ])
          toast.dismiss()
        } catch (error) {
          toast.dismiss()
          console.log(error)
        }
      }

      toast.loading('Processing..')

      roles.length > 0 && paymentTypeOptions.length > 0 && setValues()
    }
  }, [updateUser, roles, paymentTypeOptions])

  return (
    <div>
      <Helmet>
        <title>
          {updateUser ? 'Update ' : 'Create '}
          User
        </title>
      </Helmet>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 cursor-pointer d-flex justify-content-between'>
          <div className='card-title m-0'>
            <h3 className='er m-0'>
              {updateUser ? 'Update User' : 'Create New User'}
              <span className='form-text'> (Login ID and Password will be auto-generated)</span>
            </h3>
          </div>
        </div>
        <form onSubmit={createUserFormik.handleSubmit}>
          <div className='row'>
            <div className='col-6'>
              <div className='card-body'>
                {/* User Type */}
                {/* {!updateUser && (
                  <div className='mb-3'>
                    <label className='required form-label'>User Type</label>

                    <ReactSelectBox
                      placeholder='Select user type'
                      formik={createUserFormik}
                      fieldValue='userTypeName'
                      options={usersType}
                      setSelect={onSelectUserType}
                    />

                    {createUserFormik.touched.userTypeName && createUserFormik.errors.userTypeName && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.userTypeName}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )} */}

                {/* Author role */}
                <div className='mb-3'>
                  <label className='required form-label'>Author Role</label>

                  <ReactSelectBox
                    placeholder='Select role'
                    formik={createUserFormik}
                    fieldValue='authorRoleName'
                    options={roles}
                    setSelect={onSelectAuthorRole}
                  />

                  {createUserFormik.touched.authorRoleName &&
                    createUserFormik.errors.authorRoleName && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.authorRoleName}</span>
                        </div>
                      </div>
                    )}
                </div>

                {/* User Name */}
                <div className='mb-3'>
                  <label className='required form-label'>Name</label>

                  <input
                    {...createUserFormik.getFieldProps('name')}
                    placeholder='Name'
                    name='name'
                    type='text'
                    className={clsx(
                      'form-control custom-input',
                      {'is-invalid': createUserFormik.touched.name && createUserFormik.errors.name},
                      {
                        'is-valid': createUserFormik.touched.name && !createUserFormik.errors.name,
                      }
                    )}
                    autoComplete='off'
                  />

                  {createUserFormik.touched.name && createUserFormik.errors.name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{createUserFormik.errors.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className='mb-3'>
                  <label className='required form-label'>Email</label>

                  <input
                    {...createUserFormik.getFieldProps('email')}
                    placeholder='Enter email'
                    name='email'
                    type='text'
                    className={clsx(
                      'form-control custom-input',
                      {
                        'is-invalid':
                          createUserFormik.touched.email && createUserFormik.errors.email,
                      },
                      {
                        'is-valid':
                          createUserFormik.touched.email && !createUserFormik.errors.email,
                      }
                    )}
                    autoComplete='off'
                  />

                  {createUserFormik.touched.email && createUserFormik.errors.email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{createUserFormik.errors.email}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Number */}
                <div className='mb-3'>
                  <label className='required form-label'>Contact Number</label>

                  <input
                    {...createUserFormik.getFieldProps('number')}
                    placeholder='Enter number'
                    name='number'
                    type='text'
                    className={clsx(
                      'form-control custom-input',
                      {
                        'is-invalid':
                          createUserFormik.touched.number && createUserFormik.errors.number,
                      },
                      {
                        'is-valid':
                          createUserFormik.touched.number && !createUserFormik.errors.number,
                      }
                    )}
                    autoComplete='off'
                  />

                  {createUserFormik.touched.number && createUserFormik.errors.number && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{createUserFormik.errors.number}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Type */}
                <div className='mb-3'>
                  <label className='required form-label'>Payment Type</label>

                  <ReactSelectBox
                    placeholder='Select payment type'
                    formik={createUserFormik}
                    fieldValue='paymentTypeName'
                    options={paymentTypeOptions}
                    setSelect={onSelectPaymentType}
                  />

                  {createUserFormik.touched.paymentTypeName &&
                    createUserFormik.errors.paymentTypeName && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.paymentTypeName}</span>
                        </div>
                      </div>
                    )}
                </div>

                {/* Credit Limit white payment type is postpaid */}
                {createUserFormik.values.paymentTypeName.toLowerCase() === 'postpaid' && (
                  <div className='mb-3'>
                    <label className='form-label required'>Credit Limit</label>

                    <input
                      {...createUserFormik.getFieldProps('creditLimit')}
                      placeholder='Enter credit limit'
                      name='creditLimit'
                      type='text'
                      className={clsx(
                        'form-control custom-input',
                        {
                          'is-invalid':
                            createUserFormik.touched.creditLimit &&
                            createUserFormik.errors.creditLimit,
                        },
                        {
                          'is-valid':
                            createUserFormik.touched.creditLimit &&
                            !createUserFormik.errors.creditLimit,
                        }
                      )}
                      autoComplete='off'
                    />

                    {createUserFormik.touched.creditLimit && createUserFormik.errors.creditLimit && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.creditLimit}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Margin Type */}
                {!updateUser && (
                  <div className='mb-3'>
                    <label className='form-label required'>Margin Type</label>

                    <select
                      className={clsx(
                        'form-select custom-input',
                        {
                          'border-danger':
                            createUserFormik.touched.marginType &&
                            createUserFormik.errors.marginType,
                        },
                        {
                          'border-success':
                            createUserFormik.touched.marginType &&
                            !createUserFormik.errors.marginType,
                        }
                      )}
                      value={createUserFormik.values.marginType}
                      onChange={handleMarginTypeChange}
                    >
                      <option value=''>Select margin type</option>
                      <option value='add+'>Add (+)</option>
                      <option value='percentage%'>Percentage (%)</option>
                    </select>

                    {createUserFormik.touched.marginType && createUserFormik.errors.marginType && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.marginType}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Profit Margin */}
                {!updateUser && (
                  <div className='mb-3'>
                    <label className='form-label required'>Profit Margin (Tk)</label>

                    <input
                      {...createUserFormik.getFieldProps('profitPer')}
                      placeholder='Enter profit'
                      name='profitPer'
                      type='text'
                      className={clsx(
                        'form-control custom-input',
                        {
                          'is-invalid':
                            createUserFormik.touched.profitPer && createUserFormik.errors.profitPer,
                        },
                        {
                          'is-valid':
                            createUserFormik.touched.profitPer &&
                            !createUserFormik.errors.profitPer,
                        }
                      )}
                      autoComplete='off'
                    />

                    {createUserFormik.touched.profitPer && createUserFormik.errors.profitPer && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.profitPer}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sender ID */}

                {!updateUser && (
                  <div className='row mb-6'>
                    <span className='er m-0 required'>Sender ID</span>
                    <div className='d-flex p-2'>
                      <DualListBox
                        options={senderIdsOptions}
                        selected={selected}
                        onChange={(labels) => {
                          setSelected(labels)
                          createUserFormik.setFieldValue('senders', labels)
                        }}
                        canFilter
                        icons={{
                          moveLeft: <>&lt;</>,
                          moveAllLeft: <>&#8810;</>,
                          moveRight: <>&gt;</>,
                          moveAllRight: <>&#8811;</>,
                        }}
                        showOrderButtons={false}
                        showHeaderLabels={false}
                        className='w-100 text-light'
                      />
                    </div>

                    {createUserFormik.touched.senders && createUserFormik.errors.senders && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.senders}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button
                  type='submit'
                  className='btn btn-primary btn-lg'
                  disabled={createUserFormik.isSubmitting}
                >
                  {updateUser ? 'Update' : 'Create'}
                </button>
              </div>
            </div>

            <div className='col-6'>
              <div className='card-body'>
                {/* Select Profile */}
                <div className='mb-12'>
                  <div className='mb-6'>
                    <label className='col-form-label required fs-6'>Profile</label>
                    <div {...getProfileRootProps({className: 'dropzone'})}>
                      <input {...getProfileInputProps()} />
                      <p className='fs-6 fw-semibold m-0'>
                        Drag 'n' drop image here, or click to select image
                      </p>
                      <p
                        className='fs-4 fw-bold text-primary'
                        onClick={() => {
                          setPreviewFiles((prevState) => ({
                            ...prevState,
                            profile: '',
                          }))
                        }}
                      >
                        Select image
                      </p>
                    </div>

                    <p className='fw-semibold mt-1'>Accept only jpg/jpeg/png image</p>
                    {createUserFormik.touched.profile && createUserFormik.errors.profile && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.profile}</span>
                        </div>
                      </div>
                    )}
                    <PreviewImage previewUrl={previewFiles.profile} file={userImg} />
                  </div>

                  {/* Select NID */}
                  <div className='mb-6'>
                    <label className='col-form-label required fs-6'>NID File</label>
                    <div {...getContactRootProps({className: 'dropzone'})}>
                      <input {...getContactInputProps()} />
                      <p className='fs-6 fw-semibold m-0'>
                        Drag 'n' drop image here, or click to select image
                      </p>
                      <p
                        className='fs-4 fw-bold text-primary'
                        onClick={() => {
                          setPreviewFiles((prevState) => ({
                            ...prevState,
                            nid: '',
                          }))
                        }}
                      >
                        Select image
                      </p>
                    </div>

                    <p className='fw-semibold mt-1'>Accept only jpg/jpeg/png image</p>
                    {createUserFormik.touched.nid && createUserFormik.errors.nid && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.nid}</span>
                        </div>
                      </div>
                    )}
                    <PreviewImage previewUrl={previewFiles.nid} file={nidFile} />
                  </div>

                  {/* Trade License */}
                  <div className='mb-6'>
                    <label className='col-form-label required fs-6'>Trade License</label>
                    <div {...getLicenseRootProps({className: 'dropzone'})}>
                      <input {...getLicenseInputProps()} />
                      <p className='fs-6 fw-semibold m-0'>
                        Drag 'n' drop image here, or click to select image
                      </p>
                      <p
                        className='fs-4 fw-bold text-primary'
                        onClick={() => {
                          setPreviewFiles((prevState) => ({
                            ...prevState,
                            trade: '',
                          }))
                        }}
                      >
                        Select image
                      </p>
                    </div>

                    <p className='fw-semibold mt-1'>Accept only jpg/jpeg/png image</p>
                    {createUserFormik.touched.trade && createUserFormik.errors.trade && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{createUserFormik.errors.trade}</span>
                        </div>
                      </div>
                    )}
                    <PreviewImage previewUrl={previewFiles.trade} file={tradeLicenseFile} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateUser
